import { useTranslation } from "react-i18next";
/* -----------------Globals--------------- */
import { Dimensions, ScrollView } from "react-native";
/* -----------------UI--------------- */
import { Text } from "react-native-paper";

import { isWeb } from "@helpers/platform";
import { Box, EmptyState } from "@memorang/ui";
import { router } from "expo-router";
import UndrawWinnersImage from "../../../../../assets/no-search-results.svg";
import type { PeriodType } from "../../api/queries";
import FilterChips from "./FilterChips";
import LeaderboardList from "./LeaderboardList";
import LeaderboardUser from "./LeaderboardUser";
import TopRankersList from "./TopRankersList";
import { filterChips } from "./constants";
import type { LeaderboardItemType } from "./types";

type Props = {
	leaderboards: LeaderboardItemType[];
	daysToReset?: string;
	userLeaderboardItem?: LeaderboardItemType;
	topRankers: LeaderboardItemType[];
	selectedChip: PeriodType;
	avatarType?: "initials" | "identicon";
	handleSelectChip: (type: PeriodType) => void;
};

const LeaderboardView = (props: Props) => {
	const { t } = useTranslation();
	const {
		leaderboards,
		daysToReset,
		topRankers,
		userLeaderboardItem,
		selectedChip,
		handleSelectChip,
		avatarType,
	} = props;

	if (!leaderboards?.length) {
		return null;
	}

	const LeaderboardCaption = ({ days }: { days: string }) => (
		<Text variant="bodySmall">{t("leaderboard.resetIn", { days })}</Text>
	);

	const RenderEmptyState = () => {
		return (
			<Box flexGrow={1} display="flex" justifyContent="center">
				<EmptyState
					title={t("emptyState.leaderboard.title")}
					subtitle={t("emptyState.leaderboard.subtitle")}
					image={UndrawWinnersImage}
					cta="Study Now"
					handleAction={() => {
						router.push("/home");
					}}
				/>
			</Box>
		);
	};

	return (
		<Box
			flexGrow={1}
			height="100%"
			justifyContent="space-between"
			maxHeight={isWeb ? Dimensions.get("window").height - 200 : undefined}
		>
			<ScrollView
				contentContainerStyle={{
					flexGrow: 1,
					paddingVertical: 16,
					gap: 16,
				}}
				showsVerticalScrollIndicator={false}
			>
				{selectedChip !== "all_time" || leaderboards.length > 3 ? (
					<FilterChips
						chips={filterChips}
						selectedChipId={selectedChip}
						handleSelectChip={(chipId: string) =>
							handleSelectChip(chipId as PeriodType)
						}
					/>
				) : null}
				{leaderboards.length > 3 ? (
					<Box paddingTop={16}>
						<Box paddingHorizontal={16}>
							<TopRankersList avatarType={avatarType} topRankers={topRankers} />
						</Box>
						{daysToReset ? <LeaderboardCaption days={daysToReset} /> : null}
						<LeaderboardList
							avatarType={avatarType}
							leaderboards={leaderboards}
						/>
					</Box>
				) : (
					<RenderEmptyState />
				)}
			</ScrollView>
			{userLeaderboardItem && (
				<Box paddingHorizontal={16} paddingBottom={16}>
					<LeaderboardUser
						leaderboardItem={userLeaderboardItem}
						avatarType={avatarType}
					/>
				</Box>
			)}
		</Box>
	);
};

export default LeaderboardView;
